import React, { useEffect, useRef, useState } from 'react';
import { StatusBar } from 'expo-status-bar';
import { StyleSheet, Text, View, Platform, TextInput, TouchableOpacity, Animated, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { saveEmailCapture } from './firebaseConfig';
import LogoSvg from './LogoSvg';
import { useNavigation } from './context/NavigationContext';

export default function MainPage() {
  const { navigateTo } = useNavigation();
  const [hovered, setHovered] = useState(false);
  const entryAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const shimmerLoopRef = useRef(null);

  useEffect(() => {
    Animated.timing(entryAnim, {
      toValue: 1,
      duration: 700,
      easing: Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: true,
    }).start();
  }, [entryAnim]);

  const handleHoverIn = () => {
    setHovered(true);
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1.02, useNativeDriver: true, friction: 7, tension: 120 }),
      Animated.timing(glowAnim, { toValue: 1, duration: 220, easing: Easing.out(Easing.cubic), useNativeDriver: false }),
    ]).start();
    shimmerAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(shimmerAnim, { toValue: 1, duration: 1400, easing: Easing.linear, useNativeDriver: true })
    );
    shimmerLoopRef.current = loop;
    loop.start();
  };

  const handleHoverOut = () => {
    setHovered(false);
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true, friction: 7, tension: 120 }),
      Animated.timing(glowAnim, { toValue: 0, duration: 220, easing: Easing.in(Easing.cubic), useNativeDriver: false }),
    ]).start();
    if (shimmerLoopRef.current) shimmerLoopRef.current.stop();
  };

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onNotify = async () => {
    if (!email || sending) return;
    try {
      setSending(true);
      await saveEmailCapture(email.trim());
      setSent(true);
      setEmail('');
    } catch (e) {
      console.warn('Failed to save email', e);
    } finally {
      setSending(false);
    }
  };
  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Removed purple gradient accent; stick to pure black/white theme */}

      <Animated.View
        style={[
          styles.cardShadow,
          { transform: [{ translateY: entryAnim.interpolate({ inputRange: [0, 1], outputRange: [12, 0] }) }, { scale: scaleAnim }], opacity: entryAnim }
        ]}
        {...(Platform.OS === 'web'
          ? { onMouseEnter: handleHoverIn, onMouseLeave: handleHoverOut }
          : {})}
      >
        <BlurView intensity={60} tint="dark" style={styles.glassCard}>
          <View pointerEvents="none" style={styles.bevelOverlay} />

          {/* Hover-only gloss on web; default is no gloss */}
          <View style={styles.hoverWrap}>
            <Animated.View style={Platform.OS === 'web' ? { opacity: glowAnim } : { opacity: 0 } } pointerEvents="none">
              <LinearGradient
                colors={['rgba(255,255,255,0.24)', 'rgba(255,255,255,0.08)', 'rgba(255,255,255,0.0)']}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={styles.topGloss}
              />
            </Animated.View>
            <Animated.View style={Platform.OS === 'web' ? { opacity: glowAnim } : { opacity: 0 } } pointerEvents="none">
              <LinearGradient
                colors={['rgba(255,255,255,0.0)', 'rgba(255,255,255,0.08)']}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={styles.innerGlow}
              />
            </Animated.View>
            {/* shimmer sweep */}
            {Platform.OS === 'web' ? (
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.shimmer,
                  { transform: [{ translateX: shimmerAnim.interpolate({ inputRange: [0, 1], outputRange: [-300, 600] }) }] },
                  { opacity: glowAnim }
                ]}
              >
                <LinearGradient
                  colors={[
                    'rgba(255,255,255,0.0)',
                    'rgba(255,255,255,0.18)',
                    'rgba(255,255,255,0.0)'
                  ]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{ flex: 1, borderRadius: 24 }}
                />
              </Animated.View>
            ) : null}
          </View>

          <View style={styles.logoWrap}>
            <LogoSvg width={64} height={64} color={'rgba(255,255,255,0.9)'} />
          </View>
          <Text style={styles.title}>Coming Soon</Text>
          <Text style={styles.subtitle}>We’re crafting something exceptional.</Text>

          {/* Notify form */}
          <View style={styles.formRow}>
            <TextInput
              placeholder="Enter your email"
              placeholderTextColor="rgba(255,255,255,0.6)"
              style={styles.input}
              keyboardType="email-address"
              autoCapitalize="none"
              value={email}
              onChangeText={setEmail}
            />
            <TouchableOpacity activeOpacity={0.8} style={styles.ctaButton} onPress={onNotify} disabled={sending || sent}>
              <Text style={styles.ctaText}>{sent ? 'Added' : (sending ? 'Saving...' : 'Notify me')}</Text>
            </TouchableOpacity>
          </View>

          {/* Administrator Login */}
          <View style={styles.adminSection}>
            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>Alpha Testers</Text>
              <View style={styles.dividerLine} />
            </View>
            <TouchableOpacity 
              activeOpacity={0.8} 
              style={styles.adminButton} 
              onPress={() => navigateTo('Login')}
            >
              <Text style={styles.adminButtonIcon}>🔐</Text>
              <Text style={styles.adminButtonText}>Admin & Tester Login</Text>
            </TouchableOpacity>
            <Text style={styles.adminHint}>
              For approved users only
            </Text>
          </View>
        </BlurView>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  accentGlow: {
    position: 'absolute',
    width: 600,
    height: 600,
    borderRadius: 600,
    top: -150,
    left: -150,
    opacity: 0.6,
  },
  cardShadow: {
    width: '85%',
    maxWidth: 520,
    marginHorizontal: 'auto',
    borderRadius: 24,
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 36,
    shadowOffset: { width: 0, height: 18 },
  },
  glassCard: {
    width: '100%',
    minHeight: 180,
    paddingVertical: 32,
    paddingHorizontal: 28,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.35)',
    borderLeftColor: 'rgba(255, 255, 255, 0.22)',
    borderRightColor: 'rgba(0, 0, 0, 0.35)',
    borderBottomColor: 'rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        transitionProperty: 'box-shadow, background-color',
        transitionDuration: '180ms',
      }
    })
  },
  bevelOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 24,
    borderTopColor: 'rgba(255,255,255,0.3)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(0,0,0,0.3)',
    borderBottomColor: 'rgba(0,0,0,0.4)',
    borderWidth: 1,
  },
  topGloss: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '55%',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  innerGlow: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    height: '45%',
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
  },
  innerGlowBW: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    height: '45%',
    backgroundColor: 'rgba(255,255,255,0.06)'
  },
  hoverWrap: {
    ...Platform.select({
      web: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }
    })
  },
  
  title: {
    color: '#fff',
    fontSize: 36,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: 0.5,
    ...(Platform.OS === 'web'
      ? { fontFamily: "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" }
      : {}),
  },
  logoWrap: {
    marginBottom: 12,
    alignItems: 'center',
    justifyContent: 'center'
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: 16,
    marginTop: 10,
    textAlign: 'center',
    ...(Platform.OS === 'web'
      ? { fontFamily: "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" }
      : {}),
  },
  formRow: {
    marginTop: 18,
    width: '100%',
    maxWidth: 520,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  input: {
    flex: 1,
    height: 46,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.35)',
    color: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)'
  },
  ctaButton: {
    paddingHorizontal: 16,
    height: 46,
    borderRadius: 12,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center'
  },
  ctaText: {
    color: '#000',
    fontWeight: '700'
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 160,
    transform: [{ rotate: '20deg' }],
    opacity: 0.0,
  },
  adminSection: {
    marginTop: 32,
    width: '100%',
    alignItems: 'center',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  dividerText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingHorizontal: 12,
  },
  adminButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    gap: 8,
  },
  adminButtonIcon: {
    fontSize: 18,
  },
  adminButtonText: {
    color: '#a78bfa',
    fontSize: 14,
    fontWeight: '700',
  },
  adminHint: {
    marginTop: 8,
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    fontStyle: 'italic',
  },
});


